import {useMemo, useState} from 'react';
import {Task} from '../../../..';
import {useTasksOperations} from '../../../../entities';
import {
  InitialScheduleEditState,
  ScheduleEditOperations,
  ScheduleEditState,
} from '../../types';
import {dragDropReducer} from './functions';

export const useEditScheduleDataContext = (
  originalTasks: Task[],
): ScheduleEditOperations => {
  const [editState, setEditState] = useState<ScheduleEditState>(
    InitialScheduleEditState,
  );
  // Calculate edited tasks
  const droppedTasks = Object.keys(editState.editedTasks).map(
    taskId => editState.editedTasks[parseInt(taskId)],
  );

  const {update} = useTasksOperations();
  const operations = useMemo(
    () => ({
      // Edit operations
      cancelEdit: () =>
        setEditState(p =>
          dragDropReducer(p, {type: 'cancelEdit', payload: {originalTasks}}),
        ),
      // Commit
      commitEdit: () => {
        return Promise.all(
          droppedTasks.map(t =>
            update(t.id, {
              shiftId: t.shiftId ?? null,
              taskSequence: t.taskSequence,
            }),
          ),
        ).then(() => {
          setEditState(p =>
            dragDropReducer(p, {type: 'cancelEdit', payload: {originalTasks}}),
          );
        });
      },
      // When starting to drag a task
      beginDragTask: (taskId: number) => {
        setEditState(p =>
          dragDropReducer(p, {
            type: 'beginDrag',
            payload: {taskId, originalTasks},
          }),
        );
      },
      // The current drag operation was cancelled. Should also
      // cancel edit state if the edit state is equal to the state
      cancelDragTask: (taskId: number) => {
        setEditState(p =>
          dragDropReducer(p, {
            type: 'cancelDrag',
            payload: {taskId, originalTasks},
          }),
        );
      },
      // When shift receives a begin hover task
      beginHoverTaskOverShift: (
        taskId: number,
        shiftId: number,
        taskSequence: number,
      ) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'beginHover',
            payload: {taskId, shiftId, taskSequence, originalTasks},
          }),
        ),
      // When shift receives a hover task
      hoverTaskOverShift: (
        taskId: number,
        shiftId: number,
        taskSequence: number,
      ) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'hover',
            payload: {taskId, shiftId, taskSequence, originalTasks},
          }),
        ),
      // When shift receives an end hover task (task is no longer in shift)
      endHoverTaskOverShift: (taskId: number, shiftId: number) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'endHover',
            payload: {taskId, shiftId, originalTasks},
          }),
        ),
      // A task is dropped in a shift
      dropTaskOnShift: (
        taskId: number,
        shiftId: number,
        taskSequence: number,
      ) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'drop',
            payload: {taskId, shiftId, taskSequence, originalTasks},
          }),
        ),
      // When shift receives a begin hover task
      beginHoverTaskOverUnassigned: (taskId: number) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'beginHover',
            payload: {taskId, originalTasks},
          }),
        ),
      // When shift receives an end hover task (task is no longer in shift)
      endHoverTaskOverUnassigned: (taskId: number) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'endHover',
            payload: {taskId, originalTasks},
          }),
        ),
      // A task is dropped in a shift
      dropTaskOnUnassigned: (taskId: number) =>
        setEditState(p =>
          dragDropReducer(p, {
            type: 'drop',
            payload: {taskId, shiftId: -1, taskSequence: -1, originalTasks},
          }),
        ),
    }),
    [droppedTasks, originalTasks, update],
  );
  return {
    ...operations,
    editState,
    droppedTasks,
  };
};
